import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {Qualification} from '../../models/qualification';
import { ODataEntitySetService, ODataServiceFactory } from 'angular-odata';
import {ParentTableService} from '../table/parent-table.service';
import {exportable$} from '../table/decorators/exportable';
import {QualificationToAdd} from '../../features/qualification/models/qualification';
import {Observable} from 'rxjs';
import {ChinaDetails, QualificationPackage, QualificationResponse} from '../../models/qualification-response';
import {QualificationArchive} from '../../models/qualification-archive';
import {map} from 'rxjs/operators';
import {ODataResponseMapper} from '../table/mappers/odata-response-mapper';
import {QUALIFICATION_FLOW, QUALIFICATION_FLOW_STORAGE} from '../table/configuration/constants';
import {HelperFunctions} from '../table/configuration/helper-functions';
import {SQDataConfiguration} from '../table/configuration/OData-configuration';

@Injectable()
export class QualificationService extends ParentTableService {
  private config: SQDataConfiguration;
  private odata: ODataEntitySetService<Qualification>;
  private odataExt: ODataEntitySetService<Qualification>;
  @exportable$ qualification: any;

  constructor(private odataFactory: ODataServiceFactory, private httpService: HttpClient) {
    super('QualificationService');
    this.config = new SQDataConfiguration();
    this.odata = this.odataFactory.entitySet<Qualification>('Overview(0)/Qualification');
    this.odataExt = this.odataFactory.entitySet<Qualification>('Overview(0)/ExtQualification');
  }

  getData(pageIndex, count, sortModel, filterModel) {
    const orders = [];
    sortModel.forEach(x => orders.push(x.colId.replace(/\./g, '/') + ' ' + x.sort));

    let filter = '';
    filterModel = {
      ...filterModel, ...{
        CurrentFlow: {
          filterType: 'number',
          type: 'equals',
          filter: localStorage.getItem(QUALIFICATION_FLOW_STORAGE) ?? QUALIFICATION_FLOW.Standard
        }
      }
    };
    let dashboard = '';
    if (filterModel.chartCode) {
      dashboard = HelperFunctions.getDashboardFiltersQuery(filterModel.chartCode);
    }
    filter = HelperFunctions.addOdataFilters(filter, filterModel);

    const query = this.odata.entities().query(q => {
      q.top(count);
      q.skip(count * pageIndex);
      if (orders.length > 0) q.orderBy(orders);
      if (filter?.length) q.filter(filter);
      return q;
    });

    this.qualification = query;
    return this.httpService.get(`${this.qualification.api.serviceRootUrl + this.qualification.toString()}&$count=true${dashboard}`);
  }

  deleteQualification(id: number) {
    return this.httpService.delete(this.config.apiBaseUrl + 'Qualification/' + id);
  }

  getSupplierQualifications(id: number): any {
    return this.odata.entities().query(q => {
      q.filter(`CurrentFlow eq ${QUALIFICATION_FLOW.Standard} AND SupplierId eq ${id}`);
      return q;
    }).fetch({ withCount: true }).pipe(map((r: any) => ODataResponseMapper.mapResponse(r)));
  }

  getSupplierQualificationsExternal(): any {
    return this.odataExt.entities().query(q => q).fetch({ withCount: true }).pipe(map((r: any) => ODataResponseMapper.mapResponse(r)));
  }

  getQualificationById(id: number): Observable<QualificationResponse> {
    return this.httpService.get<QualificationResponse>(this.config.apiBaseUrl + 'Qualification/' + id);
  }

  addQualification(qualification: QualificationToAdd, technology: number) {
    return this.httpService.post(this.config.apiBaseUrl + 'Qualification/Add()', qualification, {params: {technology: technology}});
  }

  updateQualification(id: number, qualification: QualificationToAdd, technology: number) {
    return this.httpService.post(this.config.apiBaseUrl + 'Qualification/Update(' + id + ')', this.removeNullValues(qualification), {params: {technology: technology}});
  }

  updateQualificationLanguage(id: number, languageId: number) {
    return this.httpService.post(this.config.apiBaseUrl + `Qualification/${id}/Language(${languageId})`, null);
  }

  exportQualificationToExcel(id: number) {
    return this.httpService.get(
      this.config.apiBaseUrl + 'Qualification/Export(' + id + ')',
      {responseType: 'blob'});
  }
  exportQualificationToEmail(id: number, recipients: string[]) {
    return this.httpService.post(this.config.apiBaseUrl + 'Qualification/Email/Export(' + id + ')', recipients);
  }

  getValueStream(id: number, valueStreamPictureRequest: any) {
    let query = '?';
    if (valueStreamPictureRequest.isRg4) {query += 'isRg4=true&'; }
    if (valueStreamPictureRequest.isRg5) {query += 'isRg5=true&'; }
    if (valueStreamPictureRequest.isRg6) {query += 'isRg6=true'; }
    return this.httpService.get(this.config.apiBaseUrl + 'Qualification/' + id + '/LopaStreamValue' + query,
      {responseType: 'blob'});
  }

  getQualificationArchive(qualificationResponseId: number, snapshotId: string = null) {
    let query = '';
    if (snapshotId) {
      query += '?snapshotId=' + snapshotId;
    }
    return this.httpService.get<Array<QualificationArchive>>(this.config.apiBaseUrl +
      'Qualification/' + qualificationResponseId + '/Archive' + query);
  }

  getPackage(id: number) {
    return this.httpService.get<any>(this.config.apiBaseUrl + 'Package/' + id);
  }

  addWorkPackage(request: any) {
    return this.httpService.post(this.config.apiBaseUrl + 'Package', request);
  }

  updateWorkPackage(workPackage: QualificationPackage) {
    return this.httpService.post(this.config.apiBaseUrl + 'Package', workPackage);
  }

  removeWorkPackage(workPackage: QualificationPackage) {
    return this.httpService.delete(this.config.apiBaseUrl + 'Package/' + workPackage.Id);
  }

  updatePackageManualStatusFlag(pack: QualificationPackage): any {
    return this.httpService.post(this.config.apiBaseUrl + 'Package/Kpi/Update()', {
      Id: pack.Id,
      IsManualKpiUpdate: pack.IsManualKpiUpdate,
      KpiColor: pack.ManualKpiColor
    });
  }

  updateLopaPackage(pack: QualificationPackage) {
    return this.httpService.post(this.config.apiBaseUrl + 'Package/Lopa/Update()', {
      Id: pack.Id,
      IsEscalated: pack.IsEscalated,
      IsLateIndustrialization: pack.IsLateIndustrialization,
      LateIndustrializationReason: pack.LateIndustrializationReason
    });
  }

  addChinaDetails(chinaDetails: ChinaDetails) {
    return this.httpService.post(this.config.apiBaseUrl + 'ChinaDetails', chinaDetails);
  }

  deleteChinaDetails(id: number) {
    return this.httpService.delete(this.config.apiBaseUrl + 'ChinaDetails/' + id);
  }

  removeNullValues(object: any): any {
    for (const key in object) {
      if (object[key] === null || object[key] === undefined) {
        delete object[key];
      }
    }
    return object;
  }

  QualificationResponseToRequest(response: QualificationResponse): QualificationToAdd {
    const request: QualificationToAdd = {
      SupplierId: null,
      RelocationSupplierId: null,
      ScopeId: null,
      DerivativeId: null,
      QualificationMeasureId: null,
      ToolboxId: null,
      QualificationStatusId: null,
      IsNecessary: null,
      Description: null,
      Notes: null,
      IsAccepted: null,
      LastUpdatedBy: null,
      IsValidated: null,
      StartDate: null,
      EndDate: null,
      InitialFillingDueDate: null,
      ResponsibleLqsId: null,
      ProjectLeadId: null,
      LanguageId: null,
      ScopeDescription: null,
      IsRg4: null,
      Rg4DueDate: null,
      Rg4FillingDate: null,
      Rg4HasCustomDate: null,
      IsRg5: null,
      Rg5DueDate: null,
      Rg5FillingDate: null,
      Rg5HasCustomDate: null,
      IsRg6: null,
      Rg6DueDate: null,
      Rg6FillingDate: null,
      Rg6HasCustomDate: null,
      SingleTargetDate: null,
      StartProductionDate: null,
      IsOtd: null,
      IsIto: null,
      IsAfterSales: null,
      IsChina: null,
      IsActive: null,
      JisCategoryIds: null,
      JitCategoryIds: null,
      TestAreaIds: null,
      Rg4CalculationDate: null,
      Rg5CalculationDate: null,
      Rg6CalculationDate: null,
      IsDefinitionForm: null,
      IsTogglingMilestone: null,
      CurrentFlow: QUALIFICATION_FLOW.Standard,
    };
    Object.keys(request).forEach((key) => {
      request[key] = response[key];
    });
    request.SupplierId = request.SupplierId ? request.SupplierId : response.SupplierScopeDerivative?.SupplierScope?.SupplierId;
    request.ScopeId = request.ScopeId ? request.ScopeId : response.SupplierScopeDerivative?.SupplierScope?.ScopeId;
    request.DerivativeId = request.DerivativeId ? request.DerivativeId : response.SupplierScopeDerivative?.DerivativeId;
    request.ToolboxId = request.ToolboxId ? request.ToolboxId : response.ToolboxVersion.ToolboxId;
    request.Technology = response.Technology;
    return request;
  }

  getLanguages(): Observable<any> {
    return this.httpService.get<Array<any>>(this.config.baseUrl + '/Language');
  }
}
